import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import {
  SelectedSchoolFee,
  SchoolFeeScenario,
  SchoolFeeStudentResult,
} from "../models/schoolFee";
import { DEFAULT_PHONE_COUNTRY } from "../constants/phoneCountries";

// A named step type rather than another bare integer. The payment journey's
// `currentStep` is a number whose raw setter four components reach into, and
// this is the moment the repository's own guideline says not to repeat that.
export const SCHOOL_FEE_STEPS = [
  "identity",
  "fee",
  "amount",
  "method",
  "confirm",
] as const;

export type SchoolFeeStep = (typeof SCHOOL_FEE_STEPS)[number];

// The steps live in context and only the outcome is a URL (D-20), matching how
// fund deposit already works.
type SchoolFeeContextType = {
  currentStep: SchoolFeeStep;
  goTo: (step: SchoolFeeStep) => void;
  // The journey's OWN ordered steps, because the three do not share one
  // sequence: registration has no amount step, so stepping back through the
  // full list would land a payer on a screen their journey never draws.
  // Defaulted, so a caller with a single sequence need not pass one.
  back: (steps?: readonly SchoolFeeStep[]) => void;
  canEnter: (step: SchoolFeeStep) => boolean;
  reset: () => void;

  scenario: SchoolFeeScenario;
  setScenario: (scenario: SchoolFeeScenario) => void;

  student: SchoolFeeStudentResult | null;
  setStudent: (student: SchoolFeeStudentResult | null) => void;
  studentUuid: string;
  setStudentUuid: (uuid: string) => void;
  // The search session the selected hit came from. Sent as `handler_id` and
  // compared server-side against `student_uuid` (D-HARM-6), so the two must
  // always come from the SAME hit -- which is why `selectStudent` below is the
  // only thing that writes either on the searched journey.
  handlerId: string;
  setHandlerId: (handlerId: string) => void;
  // Takes a whole search hit and writes the identity it carries as one unit.
  // A mismatched pair is refused by the server before any charge, so pairing
  // them by hand at each call site is a defect waiting for the second caller.
  selectStudent: (hit: SchoolFeeStudentResult | null) => void;
  // Drops the lapsed search session and returns to the search step, keeping
  // the pupil and the fee the payer had already chosen.
  expireSession: () => void;
  matricule: string;
  setMatricule: (matricule: string) => void;
  firstName: string;
  setFirstName: (name: string) => void;
  lastName: string;
  setLastName: (name: string) => void;
  dateOfBirth: string;
  setDateOfBirth: (date: string) => void;

  selectionId: string;
  setSelectionId: (selectionId: string) => void;

  selectedFee: SelectedSchoolFee | null;
  selectFee: (fee: SelectedSchoolFee | null) => void;

  amount: string;
  setAmount: (amount: string) => void;

  paymentMethodId: string;
  setPaymentMethodId: (id: string) => void;
  // The NATIONAL number the payer typed, and the country that decides how long
  // it must be. Named after the instrument it charges rather than after the
  // payer, because it becomes `instrument_account_number` (D-HARM-2) and a
  // local called `phone` feeding that field is how the rename gets undone.
  instrumentAccountNumber: string;
  setInstrumentAccountNumber: (number: string) => void;
  instrumentCountry: string;
  setInstrumentCountry: (code: string) => void;
  email: string;
  setEmail: (email: string) => void;
};

const SchoolFeeContext = createContext<SchoolFeeContextType | undefined>(
  undefined
);

export const useSchoolFee = () => {
  const context = useContext(SchoolFeeContext);

  // No default value: a step reading an empty journey would look like an empty
  // result rather than a wiring mistake.
  if (!context) {
    throw new Error("useSchoolFee must be used within a SchoolFeeProvider");
  }

  return context;
};

export const SchoolFeeProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [currentStep, setCurrentStep] = useState<SchoolFeeStep>("identity");
  const [scenario, setScenario] = useState<SchoolFeeScenario>("A");
  const [student, setStudent] = useState<SchoolFeeStudentResult | null>(null);
  const [studentUuid, setStudentUuid] = useState("");
  const [matricule, setMatricule] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [selectionId, setSelectionId] = useState("");
  const [selectedFee, setSelectedFee] =
    useState<SelectedSchoolFee | null>(null);
  const [amount, setAmount] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState("");
  const [handlerId, setHandlerId] = useState("");
  const [instrumentAccountNumber, setInstrumentAccountNumber] = useState("");
  const [instrumentCountry, setInstrumentCountry] = useState(
    DEFAULT_PHONE_COUNTRY
  );
  const [email, setEmail] = useState("");

  // Scenario A is identified by a student the search resolved; B and C build
  // the identity by hand, so what counts as "identified" differs.
  const identified =
    scenario === "A" ? !!studentUuid : !!selectionId || !!matricule;

  const canEnter = useCallback(
    (step: SchoolFeeStep) => {
      switch (step) {
        case "identity":
          return true;
        case "fee":
          return identified;
        case "amount":
          return identified && !!selectedFee;
        case "method":
          return identified && !!selectedFee && !!amount;
        case "confirm":
          return (
            identified &&
            !!selectedFee &&
            !!amount &&
            !!paymentMethodId &&
            !!instrumentAccountNumber
          );
        default:
          return false;
      }
    },
    [
      identified,
      selectedFee,
      amount,
      paymentMethodId,
      instrumentAccountNumber,
    ]
  );

  // No step without its predecessor's output. Refusing here rather than in
  // each screen means a step cannot be entered by a stray navigation.
  const goTo = useCallback(
    (step: SchoolFeeStep) => {
      if (!canEnter(step)) return;
      setCurrentStep(step);
    },
    [canEnter]
  );

  // ⛔ Never wire this straight to an onClick: the handler would hand it the
  // MouseEvent as its step list. The caller passes its own sequence.
  const back = useCallback(
    (steps: readonly SchoolFeeStep[] = SCHOOL_FEE_STEPS) => {
      setCurrentStep(current => {
        const index = steps.indexOf(current);
        return index > 0 ? steps[index - 1] : current;
      });
    },
    []
  );

  // Changing the fee drops the amount with it: an amount priced against the
  // fee the payer just abandoned is the wrong number, and keeping it is how a
  // partial payment ends up validated against the wrong ceiling.
  const selectFee = useCallback((fee: SelectedSchoolFee | null) => {
    setSelectedFee(previous => {
      if (previous?.fee_assignment_id !== fee?.fee_assignment_id) setAmount("");
      return fee;
    });
  }, []);

  // The three values the server cross-checks against one another travel
  // together or not at all.
  //
  // The fee is dropped when the PUPIL changes, because fee assignment ids
  // belong to a pupil and carrying one across would pay one student's fee
  // under another's identity. It is deliberately KEPT when the same pupil is
  // re-selected: that is what happens after a 300-second search session lapses
  // (D-HARM-5), and making the payer choose the fee again is the part of the
  // expiry recovery that would feel like starting over.
  const selectStudent = useCallback(
    (hit: SchoolFeeStudentResult | null) => {
      const uuid = hit?.student.student_uuid ?? "";

      // Compared against the closed-over value rather than inside a state
      // updater: an updater must be pure, and React may run it twice.
      if (uuid !== studentUuid) selectFee(null);

      setStudent(hit);
      setStudentUuid(uuid);
      setHandlerId(hit?.handler_id ?? "");

      // The search result IS the identity step's output, so choosing one moves
      // the journey on. It is done HERE rather than by the caller because
      // `goTo` closes over the gate as it was BEFORE this update: a caller
      // that selected and then called goTo("fee") in one handler would be
      // refused by a `canEnter` that had not yet seen the student.
      if (hit) setCurrentStep("fee");
    },
    [selectFee, studentUuid]
  );

  // A lapsed search session, and nothing else.
  //
  // ⛔ Only the handler is dropped. The pupil is still the pupil and the fee is
  // still theirs -- clearing `studentUuid` here would make the payer's next
  // selection look like a DIFFERENT student to `selectStudent`, which would
  // drop the fee and turn the recovery back into starting over. The empty
  // handler is what blocks pay until a fresh search replaces it.
  const expireSession = useCallback(() => {
    setHandlerId("");
    setCurrentStep("identity");
  }, []);

  const reset = useCallback(() => {
    setCurrentStep("identity");
    setScenario("A");
    setStudent(null);
    setStudentUuid("");
    setHandlerId("");
    setMatricule("");
    setFirstName("");
    setLastName("");
    setDateOfBirth("");
    setSelectionId("");
    setSelectedFee(null);
    setAmount("");
    setPaymentMethodId("");
    setInstrumentAccountNumber("");
    setInstrumentCountry(DEFAULT_PHONE_COUNTRY);
    setEmail("");
  }, []);

  const value = useMemo(
    () => ({
      currentStep,
      goTo,
      back,
      canEnter,
      reset,
      scenario,
      setScenario,
      student,
      setStudent,
      studentUuid,
      setStudentUuid,
      handlerId,
      setHandlerId,
      selectStudent,
      expireSession,
      matricule,
      setMatricule,
      firstName,
      setFirstName,
      lastName,
      setLastName,
      dateOfBirth,
      setDateOfBirth,
      selectionId,
      setSelectionId,
      selectedFee,
      selectFee,
      amount,
      setAmount,
      paymentMethodId,
      setPaymentMethodId,
      instrumentAccountNumber,
      setInstrumentAccountNumber,
      instrumentCountry,
      setInstrumentCountry,
      email,
      setEmail,
    }),
    [
      currentStep,
      goTo,
      back,
      canEnter,
      reset,
      scenario,
      student,
      studentUuid,
      handlerId,
      selectStudent,
      expireSession,
      matricule,
      firstName,
      lastName,
      dateOfBirth,
      selectionId,
      selectedFee,
      selectFee,
      amount,
      paymentMethodId,
      instrumentAccountNumber,
      instrumentCountry,
      email,
    ]
  );

  return (
    <SchoolFeeContext.Provider value={value}>
      {children}
    </SchoolFeeContext.Provider>
  );
};
